import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { WarehouseLookupEntry } from '@/types/adminWarehouseLookups'

interface WarehouseLookupEntryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode: 'create' | 'edit'
  isPending: boolean
  entry?: WarehouseLookupEntry | null
  onSubmit: (payload: {
    lookup_key: string
    value_text: string | null
    value_num: number | null
    is_active?: boolean
  }) => void
  usageBanner?: React.ReactNode
}

export default function WarehouseLookupEntryDialog({
  open,
  onOpenChange,
  mode,
  isPending,
  entry,
  onSubmit,
  usageBanner,
}: WarehouseLookupEntryDialogProps) {
  const [lookupKey, setLookupKey] = useState('')
  const [valueText, setValueText] = useState('')
  const [valueNum, setValueNum] = useState('')
  const [isActive, setIsActive] = useState(true)

  useEffect(() => {
    if (!open) {
      return
    }

    setLookupKey(entry?.lookup_key ?? '')
    setValueText(entry?.value_text ?? '')
    setValueNum(
      typeof entry?.value_num === 'number' ? String(entry.value_num) : ''
    )
    setIsActive(entry?.is_active ?? true)
  }, [open, entry])

  const title =
    mode === 'create' ? 'Add Warehouse Entry' : 'Edit Warehouse Entry'

  const submitLabel = mode === 'create' ? 'Create Entry' : 'Save Changes'

  const handleSubmit = () => {
    const trimmedKey = lookupKey.trim()

    if (!trimmedKey) {
      return
    }

    const trimmedText = valueText.trim()
    const trimmedNum = valueNum.trim()

    const parsedNum =
      trimmedNum.length > 0 && !Number.isNaN(Number(trimmedNum))
        ? Number(trimmedNum)
        : null

    onSubmit({
      lookup_key: trimmedKey,
      value_text: trimmedText.length ? trimmedText : null,
      value_num: parsedNum,
      ...(mode === 'edit' ? { is_active: isActive } : {}),
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[540px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="warehouse-lookup-key">Lookup Key</Label>
            <Input
              id="warehouse-lookup-key"
              placeholder="e.g. CN_STANDARD"
              value={lookupKey}
              onChange={e => setLookupKey(e.target.value)}
              disabled={isPending}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="warehouse-value-text">Text Value</Label>
            <Input
              id="warehouse-value-text"
              placeholder="Optional text value"
              value={valueText}
              onChange={e => setValueText(e.target.value)}
              disabled={isPending}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="warehouse-value-num">Numeric Value</Label>
            <Input
              id="warehouse-value-num"
              type="number"
              step="any"
              placeholder="Optional numeric value"
              value={valueNum}
              onChange={e => setValueNum(e.target.value)}
              disabled={isPending}
            />
          </div>

          {mode === 'edit' && usageBanner}

          {mode === 'edit' ? (
            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div>
                <p className="text-sm font-medium text-foreground">
                  Active status
                </p>
                <p className="text-xs text-muted-foreground">
                  Inactive entries are excluded by default.
                </p>
              </div>
              <Button
                type="button"
                variant={isActive ? 'secondary' : 'outline'}
                onClick={() => setIsActive(prev => !prev)}
                disabled={isPending}
              >
                {isActive ? 'Active' : 'Inactive'}
              </Button>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isPending || !lookupKey.trim()}
          >
            {isPending ? 'Saving...' : submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
